import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import type { AlgoliaProduct, JaneSearchState } from '@jane/search/types';
import { parseSearch } from '@jane/shared/util';

import type { SearchContext as SearchContextType } from '../../types';
import { useEnsureContextWithinProvider } from './hooks/useEnsureContextWithinProvider';

const buildSearchState = <T,>(
  initialSearchParams: JaneSearchState<T> = {}
): JaneSearchState<T> => ({
  bucketFilters: {},
  filters: {},
  page: 0,
  rangeFilters: {},
  searchText: '',
  ...initialSearchParams,
});

const SearchContext = createContext<SearchContextType<AlgoliaProduct> | null>(
  null
);

export const useSearchContext = () =>
  useEnsureContextWithinProvider(SearchContext, 'SearchContext');

type SearchProviderProps<T> = PropsWithChildren<{
  indexPrefix: string;
  onSearchStateChange?: (nextSearchState: JaneSearchState<T>) => void;
}>;

/**
 * A simplified version of the SearchProvider in @jane/search.
 *   It does not reference instantsearch, the old Algolia UI dependency.
 *   It does not sync search state with query params.
 */
export const SearchProvider = <T,>({
  indexPrefix,
  children,
  onSearchStateChange,
}: SearchProviderProps<T>) => {
  const location = useLocation();
  const [searchState, setSearchState] = useState<JaneSearchState<T>>(
    buildSearchState(parseSearch(location.search))
  );

  const setState = useCallback(
    (nextSearchState: Partial<JaneSearchState<T>>) => {
      setSearchState((prevSearchState) => {
        const searchState = { ...prevSearchState, ...nextSearchState };

        onSearchStateChange?.(searchState);

        return searchState;
      });
    },
    [onSearchStateChange]
  );

  const resetSearchState = useCallback(
    (nextSearchState: Partial<JaneSearchState<T>> = buildSearchState({})) => {
      setSearchState(() => {
        onSearchStateChange?.(nextSearchState);

        return nextSearchState;
      });
    },
    [onSearchStateChange]
  );

  const contextValue = useMemo(
    () => ({
      indexPrefix,
      resetSearchState,
      searchState,
      setSearchState: setState,
    }),
    [indexPrefix, resetSearchState, searchState, setState]
  );

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
};
